import {
    Component,
    Injectable,
    OnInit,
    inject
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";

import {
    fromEvent,
    lastValueFrom
} from "rxjs";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    CustomForm,
    CustomFormControl,
    CustomFormControlProps,
    CustomFormGroup,
    KeyValueIconImage,
    TilbyMagicFormComponent
} from "@tilby/tilby-ui-lib/components/tilby-magic-form";

import {
    NewEditRoomForm
} from "src/app/features/tables/rooms.state.service";

import {
    Items,
    Rooms
} from "tilby-models";

import {
    itemSelector
} from "app/ajs-upgraded-providers";

import {
    DevLogger
} from "src/app/shared/dev-logger";

import {
    Validators
} from "@angular/forms";

import {
    ConfigurationManagerService,
    EntityManagerService,
    FLOORS
} from "src/app/core";

import {
    KeyValue
} from "@angular/common";

import {
    TranslateService
} from "@ngx-translate/core";

type NewEditRoomsDialogData = {
    title: string;
    isEdit: boolean;
    items: Items[];
    selectedRoom: Rooms | undefined;
};

type coverItemData = {
    id: number;
    name: string;
}

@Component({
    selector: "app-new-edit-rooms-dialog",
    templateUrl: "./new-edit-rooms-dialog.component.html",
    styleUrls: ["./new-edit-rooms-dialog.component.scss"],
    standalone: true,
    imports: [
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        MatDialogModule,
        TilbyMagicFormComponent
    ],
    providers: [],
})
export class NewEditRoomsDialogComponent implements OnInit {
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly translateService = inject(TranslateService);

    protected readonly data: NewEditRoomsDialogData = inject(MAT_DIALOG_DATA);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly itemSelectorService = inject(itemSelector);

    private items = this.data.items;
    private isEdit = this.data.isEdit;
    private translationPrefix = this.isEdit ? 'TABLES.EDIT_ROOM' : 'TABLES.NEW_ROOM';
    private selectedRoom = this.data.selectedRoom;
    private selectedCoverItem: coverItemData | undefined;

    protected title = this.data.title;
    protected roomForm = this.createRoomForm();

    private disableFormControl(formControl: CustomFormControl) {
        formControl.customProps.inputType = 'hidden';
        formControl.clearValidators();
        formControl.setValue(null);
    }

    constructor() {
        this.roomForm.controls.cover_type.valueChanges.subscribe((cover_type: string) => {
            switch (cover_type) {
                case 'default':
                case 'none': {
                    this.disableFormControl(this.roomForm.controls.cover_value_id);
                    this.disableFormControl(this.roomForm.controls.cover_value_perc);
                }
                    break;
                case 'id': {
                    this.roomForm.controls.cover_value_id.customProps.inputType = 'text';
                    this.roomForm.controls.cover_value_id.addValidators(Validators.required);

                    this.disableFormControl(this.roomForm.controls.cover_value_perc);
                    setTimeout(() => this.listenCoverTypeClick());
                }
                    break;
                case 'perc': {
                    this.roomForm.controls.cover_value_perc.customProps.inputType = 'number';
                    this.roomForm.controls.cover_value_perc.addValidators(Validators.required);

                    this.disableFormControl(this.roomForm.controls.cover_value_id);
                }
                    break;
            }
        });
    }

    async ngOnInit() {
        if (this.selectedRoom?.cover_type === 'id') {
            this.selectedCoverItem = await this.entityManagerService.items.fetchOneOffline(this.selectedRoom.cover_value!).then(item => (item ? { id: item.id!, name: item.name } : undefined));

            if (this.selectedCoverItem) {
                this.roomForm!.controls.cover_value_id.setValue(this.selectedCoverItem.name);
            }
        }
    }

    private listenCoverTypeClick() {
        const coverTypeControllerElement = document.getElementById('cover_type_controller');
        const input = coverTypeControllerElement?.querySelector('input');

        if (coverTypeControllerElement && input) {
            fromEvent(input, 'focus').subscribe($event => input.blur());

            fromEvent(coverTypeControllerElement, 'click').subscribe($event => {
                if(this.roomForm.controls.cover_value_id.disabled) {
                    return;
                }

                $event.preventDefault();

                //Disable controller
                this.roomForm.controls.cover_value_id.disable();

                this.itemSelectorService.show(this.items).then((item: Items) => {
                    this.selectedCoverItem = { id: item.id!, name: item.name };

                    this.roomForm!.controls.cover_value_id.setValue(this.selectedCoverItem.name);
                }).finally(() => {
                    this.roomForm.controls.cover_value_id.enable();
                });
            });
        }
    }

    private createRoomForm(): CustomFormGroup<CustomForm<NewEditRoomForm>> {
        const initName = this.selectedRoom?.name || '';
        const initFloor = this.selectedRoom?.floor || 'parquet';
        const initCoverType = this.selectedRoom?.cover_type || 'default';
        const initCoverValue = this.selectedRoom?.cover_value || null;
        const initDefaultPricelist = this.selectedRoom?.default_pricelist || -1;

        const floorChoices: KeyValueIconImage<string>[] = FLOORS.map((floor) => ({
            key: '',
            value: floor.name,
            image: `assets/images/tables/floor/${floor.imageUrl}`
        }));

        const pricesList: KeyValue<string, number>[] = [
            { key: 'CUSTOMERS.DETAILS.NONE', value: -1 },
            ...Array.from({ length: 10 }, (_, i) => ({
                key: this.configurationManagerService.getPreference(`price_list_${i + 1}_name`) || 'ITEMS.DETAILS.PRICE_LIST_START_WITH_VALUE',
                value: i + 1
            }))
        ];

        const coverTypeChoices: KeyValue<string, string>[] = ['default', 'none', 'id', 'perc']?.map((cover_type: string) => ({
            key: this.translateService.instant(`${this.translationPrefix}.COVER_TYPE.${cover_type.toUpperCase()}`),
            value: cover_type
        }));

        let initCoverValueId: number | null;
        let initCoverValuePerc: number | null;

        switch (initCoverType) {
            case 'id':
                initCoverValueId = initCoverValue;
                initCoverValuePerc = null;
                break;
            case 'perc':
                initCoverValueId = null;
                initCoverValuePerc = initCoverValue;
                break;
            default:
                initCoverValueId = null;
                initCoverValuePerc = null;
                break;
        }

        let initSkipCreationDialog;

        switch (this.selectedRoom?.skip_creation_dialog) {
            case true:
                initSkipCreationDialog = 'on';
                break;
            case false:
                initSkipCreationDialog = 'off';
                break;
            default:
                initSkipCreationDialog = 'default';
                break;
        }

        return new CustomFormGroup<CustomForm<NewEditRoomForm>>({
            name: new CustomFormControl<string>(initName, { validators: [Validators.required, Validators.minLength(5), Validators.maxLength(15)] }, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.NAME`,
                hint: () => `${this.roomForm.controls.name?.value?.length || 0}/15`,
                class: 'tw-w-full'
            }),
            default_pricelist: new CustomFormControl<number>(initDefaultPricelist, {}, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.DEFAULT_PRICELIST`,
                inputType: 'select',
                inputChoices: pricesList,
                class: 'tw-w-full'
            }),
            cover_type: new CustomFormControl<string>(initCoverType, {}, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.COVER_TYPE.LABEL`,
                inputType: 'select',
                inputChoices: coverTypeChoices,
                class: 'tw-w-full'
            }),
            cover_value_id: new CustomFormControl<string | number | null>(initCoverValueId, {}, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.COVER_VALUE.ID`,
                inputType: 'text',
                inputTypeInitial: initCoverType === 'id' ? 'text' : 'hidden',
                inputInitialValue: initCoverValueId,
                id: 'cover_type_controller',
                matElementClass: 'tw-cursor-pointer',
                class: 'tw-w-full'
            }),
            cover_value_perc: new CustomFormControl<number | null>(initCoverValuePerc, {}, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.COVER_VALUE.PERC`,
                inputType: 'number',
                inputTypeInitial: initCoverType === 'perc' ? 'number' : 'hidden',
                inputConstraint: { min: 1, max: 100 },
                inputInitialValue: initCoverValuePerc,
                class: 'tw-w-full'
            }),
            floor: new CustomFormControl<string>(initFloor, { validators: [Validators.required] }, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.FLOOR`,
                inputType: 'radio',
                inputChoices: floorChoices,
                id: 'room_floor_id',
                class: 'tw-w-full'
            }),
            skip_creation_dialog: new CustomFormControl<string>(initSkipCreationDialog, {}, {
                ...new CustomFormControlProps(),
                label: `${this.translationPrefix}.SKIP_ORDER_CREATION.LABEL`,
                inputType: 'select',
                inputChoices: [
                    { key: `${this.translationPrefix}.SKIP_ORDER_CREATION.DEFAULT`, value: 'default' },
                    { key: `${this.translationPrefix}.SKIP_ORDER_CREATION.YES`, value: 'on' },
                    { key: `${this.translationPrefix}.SKIP_ORDER_CREATION.NO`, value: 'off' }
                ],
                class: 'tw-w-full'
            })
        });
    }

    protected confirm() {
        this.roomForm.markAllAsTouched();

        if (!this.roomForm.valid) {
            return;
        }

        let cover_type = this.roomForm.value.cover_type;
        let cover_value;

        switch (this.roomForm.value.cover_type) {
            case 'default':
                cover_type = undefined;
                cover_value = undefined;
                break;
            case 'none':
                cover_value = undefined;
                break;
            case 'id':
                cover_value = this.selectedCoverItem?.id;
                break;
            case 'perc':
                cover_value = parseInt(this.roomForm.value.cover_value_perc);
                break;
        }

        let skip_creation_dialog;

        switch (this.roomForm.value.skip_creation_dialog) {
            case 'on':
                skip_creation_dialog = true;
                break;
            case 'off':
                skip_creation_dialog = false;
                break;
            default:
                skip_creation_dialog = undefined;
                break;
        }

        const _default_pricelist = this.roomForm.value.default_pricelist;

        const res: Rooms = {
            cover_type: cover_type,
            cover_value: cover_value,
            default_pricelist: _default_pricelist === -1 ? null : _default_pricelist,
            floor: this.roomForm.value.floor,
            height: this.selectedRoom?.height || 10,
            name: this.roomForm.value.name,
            skip_creation_dialog: skip_creation_dialog,
            width: this.selectedRoom?.width || 10,
        };

        this.log('res', res);
        this.matDialogRef.close(res);
    }

    private log(...args: any[]) {
        DevLogger.debug('[ NewEditRoomsDialogComponent ]', ...args);
    }
}

@Injectable({
    providedIn: "root",
})
export class NewEditRoomsDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog(data: NewEditRoomsDialogData): Promise<Rooms | undefined> {
        const config: NonNullableConfigData<any> = {
            ...this.switchMobileDesktopDimensions({ width: '800px' }),
            disableClose: true,
            data,
        };
        return lastValueFrom(this.dialogRef.open(NewEditRoomsDialogComponent, config).afterClosed());
    }
}
